import * as Sentry from '@sentry/react';
import { QueryObserverResult, useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  deleteInitiative,
  partiallyUpdateInitiative,
  updateInitiative,
} from '../../../api/initiative-client/initiative-client';
import { useInitiative, useInitiativeFocus } from '../../../api/initiative-client/initiative-client.hooks';
import {
  ExtendedInitiative,
  Initiative,
  PreSaveInitiative,
} from '../../../api/initiative-client/initiative-client.type';
import type { Board, ProjectsResponse } from '../../../api/projects-client/projects-client.type';
import type { Outcome } from '../../../api/strategy-client/strategy-client.type';
import type { Epic } from '../../../api/tasks-client/task-client.type';
import { useTranslation } from 'react-i18next';
import { useGlobalStore } from '../../../store/global-store/global-store';
import {
  useCostTabStore,
  useRelatedTasksTabStore,
  useStrategyStore,
  useThroughputTabStore,
} from '../../../store/strategy-store/strategy-store';
import {
  setFocusBoardsData,
  setFocusTeamsData,
  setInitiativePerformance,
} from '../../../store/strategy-store/strategy-store.actions';
import { Panel } from '../../../store/strategy-store/strategy-store.type';
import { toast } from '../../notifications';
import { useCurrentStrategyTab } from '../strategy.hooks';
import { StrategyTab } from '../strategy.type';
import { CostMode } from './cost-tab/cost-tab.type';
import { PerformanceTab, ThroughputMode } from './initiative-performance.type';

/**
 * Hook to fetch and manage initiative performance data
 *
 * This hook fetches the extended initiative data for the currently selected initiative
 * and updates the global state with the fetched data.
 *
 * @returns {Object} An object containing the initiative performance query and loading state
 * @returns {QueryObserverResult<ExtendedInitiative, Error>} initiativePerformanceQuery - The query result for the initiative
 * @returns {boolean} isLoading - Whether the data is currently being fetched
 */
const useInitiativePerformanceData = (): {
  initiativePerformanceQuery: QueryObserverResult<ExtendedInitiative, Error>;
  isLoading: boolean;
} => {
  const i18n = useTranslation();
  const navigate = useNavigate();
  const { initiativeId } = useParams();

  const portfolio = useGlobalStore((state) => state.portfolio);

  const { query, initiative } = useInitiative(portfolio?.id, initiativeId);

  useEffect(() => {
    if (initiative) {
      setInitiativePerformance(initiative);
    }
  }, [initiative]);

  useEffect(() => {
    if (query.error) {
      navigate('/application/strategy');
      toast.error(i18n.t('strategy.initiative_performance.not_found'));
      setInitiativePerformance(null);
      Sentry.captureException(query.error);
    }
  }, [query.error, navigate, i18n.t]);

  return { initiativePerformanceQuery: query, isLoading: query.isFetching };
};

/**
 * Hook to retrieve initiative data for the performance view
 *
 * This hook attempts to retrieve initiative data from two possible sources:
 * 1. First, it checks for detailed initiative performance data in the store
 * 2. If not available, it falls back to basic initiative data from the initiatives list
 *
 * The hook uses the current route parameter to identify which initiative to retrieve.
 *
 * @returns {Initiative | ExtendedInitiative | null} The initiative data or null if not found
 */
const useInitiativePerformance = (): Initiative | ExtendedInitiative | null => {
  const { initiativeId } = useParams();

  const initiatives = useStrategyStore((state) => state.initiatives);
  const initiativePerformance = useStrategyStore((state) => state.initiativePerformance);

  const initiativeFallback = initiatives.find((initiative) => initiative.id === initiativeId) || null;

  // Require the slot's own id to match the routed initiative. The slot is polymorphic —
  // goal, outcome and epic detail pages write it too, via `any`-returning shallow copies —
  // and `location` updates a commit before the outgoing page's cleanup nulls it, so trusting
  // the slot alone yields a window where a goal is served as the current initiative.
  const initiative = initiativePerformance?.id === initiativeId ? initiativePerformance : initiativeFallback;

  return initiative;
};

/**
 * Hook to retrieve focus data for an initiative
 *
 * This hook fetches focus data for the current initiative based on the route parameter.
 * It automatically updates the global state with the fetched data and handles error reporting.
 *
 * @returns {Object} Object containing the loading state
 * @returns {boolean} isLoading - Whether the data is currently being fetched
 */
const useInitiativeFocusData = (): {
  isLoading: boolean;
} => {
  const { initiativeId } = useParams();

  const portfolio = useGlobalStore((state) => state.portfolio);

  const { focus: projectFocus, query: projectQuery } = useInitiativeFocus(portfolio?.id, initiativeId, 'project');
  const { focus: subprojectFocus, query: subprojectQuery } = useInitiativeFocus(
    portfolio?.id,
    initiativeId,
    'subproject',
  );

  useEffect(() => {
    if (projectFocus) {
      setFocusTeamsData(projectFocus);
    }
  }, [projectFocus]);

  useEffect(() => {
    if (subprojectFocus) {
      setFocusBoardsData(subprojectFocus);
    }
  }, [subprojectFocus]);

  useEffect(() => {
    if (projectQuery.error) {
      setFocusTeamsData(null);
      Sentry.captureException(projectQuery.error);
    }
  }, [projectQuery.error]);

  useEffect(() => {
    if (subprojectQuery.error) {
      setFocusBoardsData(null);
      Sentry.captureException(subprojectQuery.error);
    }
  }, [subprojectQuery.error]);

  return { isLoading: projectQuery.isFetching || subprojectQuery.isFetching };
};

/**
 * Hook to update an initiative
 *
 * This hook provides a mutation function to update an initiative's details.
 * It handles validation of required IDs and automatically invalidates related queries
 * after a successful update.
 *
 * @param {Object} params - The parameters for the hook
 * @param {string | undefined} params.portfolioId - The ID of the portfolio containing the initiative
 * @param {string | undefined} params.initiativeId - The ID of the initiative to update
 * @param {Function} [params.onSuccess] - Optional callback function to execute after successful update
 * @returns {UseMutationResult<PreSaveInitiative, Error, PreSaveInitiative>} Mutation result for updating an initiative
 */
const useEditInitiative = ({
  portfolioId,
  initiativeId,
  onSuccess,
}: {
  portfolioId: string | undefined;
  initiativeId: string | undefined;
  onSuccess?: () => void;
}): UseMutationResult<PreSaveInitiative, Error, PreSaveInitiative> => {
  const i18n = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['editInitiative', initiativeId] as const,
    mutationFn: async (initiative: PreSaveInitiative) => {
      if (!portfolioId) {
        throw new Error('Cannot update initiative: Portfolio ID is required');
      }
      if (!initiativeId) {
        throw new Error('Cannot update initiative: Initiative ID is required');
      }
      return updateInitiative(portfolioId, { id: initiativeId, ...initiative });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['initiatives', portfolioId] });
      queryClient.invalidateQueries({ queryKey: ['initiative', portfolioId, initiativeId] });
      queryClient.invalidateQueries({ queryKey: ['getInitiativeFocus', portfolioId, initiativeId] });
      queryClient.invalidateQueries({ queryKey: ['kpi-overview-data', portfolioId] });

      if (data.message) {
        toast.warning(data.message);
      } else {
        toast.success(i18n.t('strategy.initiatives.updated_success', { name: data.name }));
      }

      if (onSuccess) {
        onSuccess();
      }
    },
  });
};

const usePartiallyUpdateInitiative = ({
  portfolioId,
  initiativeId,
  onSuccess,
}: {
  portfolioId: string | undefined;
  initiativeId: string | undefined;
  onSuccess?: () => void;
}): UseMutationResult<Initiative, Error, Partial<PreSaveInitiative>> => {
  const i18n = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['partiallyUpdateInitiative', initiativeId] as const,
    mutationFn: async (data: Partial<PreSaveInitiative>) => {
      if (!portfolioId) {
        throw new Error('Cannot update initiative: Portfolio ID is required');
      }
      if (!initiativeId) {
        throw new Error('Cannot update initiative: Initiative ID is required');
      }
      return partiallyUpdateInitiative(portfolioId, initiativeId, data);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['initiatives', portfolioId] });
      queryClient.invalidateQueries({ queryKey: ['initiative', portfolioId, initiativeId] });
      queryClient.invalidateQueries({ queryKey: ['getInitiativeFocus', portfolioId, initiativeId] });
      queryClient.invalidateQueries({ queryKey: ['kpi-overview-data', portfolioId] });

      if (data.message) {
        toast.warning(data.message);
      } else {
        toast.success(i18n.t('strategy.initiatives.updated_success', { name: data.name }));
      }

      if (onSuccess) {
        onSuccess();
      }
    },
  });
};

/**
 * Hook to delete an initiative
 *
 * This hook provides a mutation function to delete an initiative.
 * It handles validation of required IDs and automatically removes and invalidates related queries
 * after a successful deletion.
 *
 * @param {Object} params - The parameters for the hook
 * @param {string | undefined} params.portfolioId - The ID of the portfolio containing the initiative
 * @param {string | undefined} params.initiativeId - The ID of the initiative to delete
 * @param {Function} [params.onSuccess] - Optional callback function to execute after successful deletion
 * @returns {UseMutationResult<void, Error, void>} Mutation result for deleting an initiative
 */
const useDeleteInitiative = ({
  portfolioId,
  initiativeId,
  onSuccess,
}: {
  portfolioId: string | undefined;
  initiativeId: string | undefined;
  onSuccess?: () => void;
}): UseMutationResult<void, Error, void> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['deleteInitiative', initiativeId] as const,
    mutationFn: async () => {
      if (!portfolioId) {
        throw new Error('Cannot delete initiative: Portfolio ID is required');
      }
      if (!initiativeId) {
        throw new Error('Cannot delete initiative: Initiative ID is required');
      }
      return deleteInitiative(portfolioId, initiativeId);
    },
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: ['initiative', initiativeId, portfolioId] });
      queryClient.invalidateQueries({ queryKey: ['initiatives', portfolioId] });

      if (onSuccess) {
        onSuccess();
      }
    },
  });
};

/**
 * Hook that retrieves epics that have contributed to the initiative
 *
 * Filters the initiative's epics to only include those that have
 * contribution data in the contributing_epics_over_time object
 *
 * @returns {Epic[]} An array of epics that have contributed to the initiative
 */
const useContributingEpics = (): Epic[] => {
  const initiative = useStrategyStore((state) => state.initiativePerformance);

  const contributingEpics = useMemo(() => {
    if (!initiative) {
      return [];
    }

    const { epics = [], contributing_epics_over_time = {} } = initiative;

    return epics.filter((epic) => contributing_epics_over_time[epic.id]);
  }, [initiative]);

  return contributingEpics;
};

/**
 * Hook that retrieves teams that have contributed to the initiative
 *
 * Filters the initiative's teams to only include those that have
 * contribution data in the contributing_teams_over_time object
 *
 * @returns {ProjectsResponse[] | []} An array of teams that have contributed to the initiative
 */
const useContributingTeams = (): ProjectsResponse[] | [] => {
  const globalTeams = useGlobalStore((state) => state.teams);
  const initiative = useStrategyStore((state) => state.initiativePerformance);

  if (!initiative) {
    return [];
  }

  const { contributing_teams_over_time = [] } = initiative;

  const contributingTeamIds = Object.keys(contributing_teams_over_time);
  const contributingTeams = globalTeams.filter((team) => contributingTeamIds.includes(team.id));

  return contributingTeams;
};

/**
 * Hook that retrieves boards that have contributed to the initiative
 *
 * Returns the subprojects from the initiative performance data,
 * which represent the boards associated with the initiative
 *
 * @returns {Board[] | []} An array of boards that have contributed to the initiative
 */
const useContributingBoards = (): Board[] | [] => {
  const initiative = useStrategyStore((state) => state.initiativePerformance);
  const currentTab = useCurrentStrategyTab();
  const globalTeams = useGlobalStore((state) => state.teams);
  const globalBoards = globalTeams.flatMap((team) => team.subprojects);

  if (!initiative) {
    return [];
  }

  if (currentTab === StrategyTab.Initiatives) {
    const { subprojects = [] } = initiative;

    return subprojects;
  }
  // goals or objectives
  const { contributing_boards_over_time = {} } = initiative;
  const contributingBoardIds = Object.keys(contributing_boards_over_time);
  return globalBoards.filter((board) => contributingBoardIds.includes(board.id));
};

/**
 * Hook that retrieves initiatives that have contributed to the initiative
 *
 * Filters the initiative's initiatives to only include those that have
 * contribution data in the contributing_initiatives_over_time object
 *
 * @returns {Initiative[]} An array of initiatives that have contributed to the initiative
 */
const useContributingInitiatives = (): Initiative[] => {
  const initiative = useStrategyStore((state) => state.initiativePerformance) as any;

  if (!initiative) {
    return [];
  }

  const { initiatives = [], contributing_initiatives_over_time = {} } = initiative;

  return initiatives.filter((initiative: Initiative) => contributing_initiatives_over_time[initiative.id]);
};

/**
 * Hook that retrieves outcomes that have contributed to the initiative
 *
 * Filters the initiative's outcomes to only include those that have
 * contribution data in the contributing_outcomes_over_time object
 *
 * @returns {Outcome[]} An array of outcomes that have contributed to the initiative
 */
const useContributingOutcomes = (): Outcome[] => {
  const initiative = useStrategyStore((state) => state.initiativePerformance) as any;

  if (!initiative) {
    return [];
  }

  const { outcomes = [], contributing_outcomes_over_time = {} } = initiative;

  return outcomes.filter((outcome: Outcome) => contributing_outcomes_over_time[outcome.id]);
};

/**
 * Hook to determine if side section should be displayed
 *
 * @returns {boolean} Whether the side section should be displayed
 */
const useSideSection = (): boolean => {
  const performanceTab = useStrategyStore((state) => state.performanceTab);
  const performanceActivePanel = useStrategyStore((state) => state.performanceActivePanel);
  const throughputMode = useThroughputTabStore((state) => state.mode);
  const throughputSelectedEpic = useThroughputTabStore((state) => state.selectedEpic);
  const throughputSelectedTeam = useThroughputTabStore((state) => state.selectedTeam);
  const throughputSelectedInitiative = useThroughputTabStore((state) => state.selectedInitiative);
  const throughputSelectedOutcome = useThroughputTabStore((state) => state.selectedOutcome);
  const costMode = useCostTabStore((state) => state.mode);
  const costSelectedEpic = useCostTabStore((state) => state.selectedEpic);
  const costSelectedTeam = useCostTabStore((state) => state.selectedTeam);
  const selectedTask = useRelatedTasksTabStore((state) => state.selectedTask);

  // Throughput tab cases
  if (performanceTab === PerformanceTab.Throughput) {
    if (throughputMode === ThroughputMode.InitiativeProgress) {
      return false;
    }

    if (throughputMode === ThroughputMode.ContributingEpics && throughputSelectedEpic) {
      return performanceActivePanel === Panel.Side;
    }

    if (throughputMode === ThroughputMode.ContributingTeams && throughputSelectedTeam) {
      return performanceActivePanel === Panel.Side;
    }

    if (throughputMode === ThroughputMode.ContributingInitiatives && throughputSelectedInitiative) {
      return performanceActivePanel === Panel.Side;
    }

    if (throughputMode === ThroughputMode.ContributingOutcomes && throughputSelectedOutcome) {
      return performanceActivePanel === Panel.Side;
    }

    return false;
  }

  // Cost tab cases
  if (performanceTab === PerformanceTab.Cost) {
    if (costMode === CostMode.ContributingEpics && costSelectedEpic) {
      return performanceActivePanel === Panel.Side;
    }

    if (costMode === CostMode.ContributingTeams && costSelectedTeam) {
      return performanceActivePanel === Panel.Side;
    }

    // Handle "Other" panels for Cost tab
    if (performanceActivePanel === Panel.OtherEpics || performanceActivePanel === Panel.OtherTeams) {
      return true;
    }

    return false;
  }

  // Related tasks tab cases
  if (performanceTab === PerformanceTab.RelatedTasks) {
    if (selectedTask) {
      return performanceActivePanel === Panel.Side;
    }

    return false;
  }

  return false;
};

export {
  useContributingBoards,
  useContributingEpics,
  useContributingInitiatives,
  useContributingOutcomes,
  useContributingTeams,
  useDeleteInitiative,
  useEditInitiative,
  useInitiativeFocusData,
  useInitiativePerformance,
  useInitiativePerformanceData,
  usePartiallyUpdateInitiative,
  useSideSection,
};
